import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { isUserCitySelector, userRoleSelector, USER_TYPES, isExternalUserSelector, isUserAdminSelector } from "redux/selectors/user";
import { fetchCityLimitSettings, fetchCitizenLimitSettings, fetchCityMailerLiteConnectedUsers, fetchCityMailerLiteSettings, getCityTenantStores, syncMailerLiteSubscriptions, updateCityLimitSettings, updateCitizenLimitSettings, updateCityMailerLiteSettings, fetchDashboardOrganisations, fetchCityDashboardOrganisationsForExport } from "services/cities";
import { completelyDeleteUser, deaffiliateUser } from "services/users";
import { toast } from 'shared/components/Toast';
import { generateFullName } from "utils";

import marketIcon from '../assets/images/marketplace.png';
import { useAuth } from "shared/providers/AuthProvider";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import { setAdminData, setDetails, updateAdminData } from "redux/actions/authAction";
import { Button } from "reactstrap";
import { Typography } from "@mui/material";
import usersHooks from "./users.hooks";
import { DASHBOARD_AVAILABLE_FILTER_TYPES } from "atomicComponents/FilterDatePicker";
import dateUtils from "utils/dateUtils";
import { readSelectedTenantId, writeSelectedTenantId } from "utils/selectedTenantStorage";
import { getEndDateForDashboard } from "containers/Dashboards/common";

const CITY_LIMIT_SETTINGS_CACHE_KEY = "gp.cityLimitSettings.cache.v1";
const cityLimitSettingsMemoryCache = new Map();

const readCityLimitSettingsCache = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    const raw = window.localStorage.getItem(CITY_LIMIT_SETTINGS_CACHE_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return;
    }

    Object.entries(parsed).forEach(([cityId, settings]) => {
      if (cityId && settings && typeof settings === "object") {
        cityLimitSettingsMemoryCache.set(cityId, settings);
      }
    });
  } catch (error) {
    console.error("Failed to read city limit settings cache", error);
  }
};

const persistCityLimitSettingsCache = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    const serialized = Object.fromEntries(cityLimitSettingsMemoryCache.entries());
    window.localStorage.setItem(CITY_LIMIT_SETTINGS_CACHE_KEY, JSON.stringify(serialized));
  } catch (error) {
    console.error("Failed to persist city limit settings cache", error);
  }
};

readCityLimitSettingsCache();

const useFetchCityLimitSettings = (cityId) => {
  const normalizedCityId = cityId ? String(cityId) : "";
  const cachedLimitSettings = normalizedCityId ? cityLimitSettingsMemoryCache.get(normalizedCityId) : undefined;
  const [isLoading, setIsLoading] = useState(() => normalizedCityId ? !cachedLimitSettings : false);
  const [limitSettings, setLimitSettings] = useState(cachedLimitSettings);


  const getLimitSettings = useCallback(async () => {
    if (!normalizedCityId) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetchCityLimitSettings(normalizedCityId);

      if (res) {
        setLimitSettings(res);
        cityLimitSettingsMemoryCache.set(normalizedCityId, res);
        persistCityLimitSettingsCache();
      }
      setIsLoading(false);
    } catch (error) {
      console.error("error", error);
      setIsLoading(false);
    }
  }, [normalizedCityId]);

  useEffect(() => {
    if (!normalizedCityId) {
      setLimitSettings(undefined);
      setIsLoading(false);
      return;
    }

    const cachedValue = cityLimitSettingsMemoryCache.get(normalizedCityId);
    if (cachedValue) {
      setLimitSettings(cachedValue);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
  }, [normalizedCityId]);

  useEffect(() => {
    getLimitSettings();
  }, [getLimitSettings]);

  return { limitSettings, isLoading };
};

const useFetchCitizenLimitSettings = (cityId) => {
  const [isLoading, setIsLoading] = useState(true);
  const [citizenLimitSettings, setCitizenLimitSettings] = useState();

  const getCitizenLimitSettings = useCallback(async () => {
    if (!cityId) return;

    try {
      const res = await fetchCitizenLimitSettings(cityId);

      if (res) {

        setCitizenLimitSettings(res);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("error", error);
      setIsLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    getCitizenLimitSettings();
  }, [getCitizenLimitSettings]);

  return { citizenLimitSettings, isLoading };
};

const useCityMailerLiteSettings = (cityId) => {
  const [isLoading, setIsLoading] = useState(true);
  const [mailerLiteSettings, setMailerLiteSettings] = useState();

  const getMailerLiteSettings = useCallback(async () => {
    if (!cityId) return;

    try {
      const res = await fetchCityMailerLiteSettings(cityId);

      if (res) {
        setMailerLiteSettings(res);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("error", error);
      setIsLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    getMailerLiteSettings();
  }, [getMailerLiteSettings]);


  const updateMailerLiteSettings = useCallback(async (settings) => updateCityMailerLiteSettings(cityId, settings), [cityId]);

  return { mailerLiteSettings, isLoading, updateMailerLiteSettings, refetchMailerLiteSettings: getMailerLiteSettings, };
};

const useUpdateCityLimitSettings = (cityId) => {
  const updateSettings = useCallback(
    async (field, settings) => updateCityLimitSettings(cityId, field, settings),
    [cityId]
  );

  return updateSettings;
};

const useUpdateCitizenLimitSettings = (cityId) => {
  const updateCitizenSettings = useCallback(
    async (field, settings) => updateCitizenLimitSettings(cityId, field, settings),
    [cityId]
  );

  return updateCitizenSettings;
};

const useDeleteUser = ({ userID, refetchUser }) => {
  const { t } = useTranslation("common");
  const loggedUserRole = useSelector(userRoleSelector);

  const handleDeleteUser = useCallback(async () => {
    if (!userID || loggedUserRole !== USER_TYPES.CITY)
      return console.log("you should be admin to do that");

    try {
      await completelyDeleteUser(userID)
      toast.success(t("admin.delete_user_success"))
      refetchUser && refetchUser()
    } catch (error) {
      toast.error(t("admin.delete_user_failed"))
      console.error('useDeleteUser', error);
    }
  }, [loggedUserRole, refetchUser, t, userID])

  return { handleDeleteUser };
}

const useDeaffiliateUser = ({ userID, refetchUser }) => {
  const { t } = useTranslation("common");

  const handleDeaffiliateUser = useCallback(async () => {
    if (!userID)
      return console.log("what is your userID");

    try {
      await deaffiliateUser(userID)
      toast.success(t("admin.deaffiliate_user_success"))
      refetchUser && refetchUser()
    } catch (error) {
      toast.error(t("admin.delete_user_failed"))
      console.error('useDeleteUser', error);
    }
  }, [refetchUser, t, userID])

  return { handleDeaffiliateUser };
}

const useFetchTenantStores = (userDetails) => {
  const [isLoading, setIsLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [, , adminData] = useAuth();
  const isCity = useSelector(isUserCitySelector);
  const isExternal = useSelector(isExternalUserSelector);
  const userId = useMemo(() => isExternal ? userDetails.cityId : userDetails.id, [isExternal, userDetails.cityId, userDetails.id]);

  // const tenantStatuses = useMemo(() => {
  //   const status = [
  //     { label: t('tenant_status.confirmed'), value: TENANTS_STATUSES.confirmed },
  //     { label: t('tenant_status.pending'), value: TENANTS_STATUSES.pending },
  //   ];

  //   return status;
  // }, [t]);

  const getAllTenantsStoresForCity = useCallback(async () => {
    try {
      const res = await getCityTenantStores(userId);


      const storesList = res.map(store => {
        const fullName = generateFullName(store.firstName || store.first_name, store.lastName || store.last_name);
        const address = store.street ? `${store.street}, ${store.city}` : store.city;

        // const statusLabel = tenantStatuses.find(status => status.value === store.status)?.label || store.status;

        return {
          image: store?.logoUrl || marketIcon,
          name: fullName,
          code: store.merchantId || '',
          address,
          //   status: statusLabel,
          ...store,
        }
      });

      setStores(storesList);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setStores([]);
      console.error('Failed to get stores', error);
    }
  }, [userId]);

  useEffect(() => {
    if (!isCity) {
      setIsLoading(false);
      return
    }
    getAllTenantsStoresForCity()
  }, [getAllTenantsStoresForCity, isCity]);

  const filteredStoresByTenantId = useMemo(() => {
    let filteredList = stores;

    if (adminData?.tenantId) {
      filteredList = stores.filter(i => i.id === adminData.tenantId);
    }

    return filteredList;
  }, [adminData?.tenantId, stores])

  return {
    isLoading,
    stores: filteredStoresByTenantId,
    allStores: stores,
    refetchStoresForCity: getAllTenantsStoresForCity,
  }
}


const useAdminDataForTenant = () => {
  const [t] = useTranslation("common");
  const dispatch = useDispatch();

  const { details: userDetails, adminData } = usersHooks.useExternalUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isCollapsed = useSelector((state) => state.sidebar.collapse);
  const isAdmin = useSelector(isUserAdminSelector);
  const [selectedStore, setSelectedStore] = useState(null);
  const tenantSelectionScopeId = useMemo(
    () => userDetails?.cityId || userDetails?.id,
    [userDetails?.cityId, userDetails?.id]
  );

  const { allStores } = cityHooks.useFetchTenantStores(userDetails);

  const onSelectTenant = useCallback(
    (store) => {
      const pathname = location?.pathname || window.location.pathname;

      if (store) {
        setSelectedStore(store);
        writeSelectedTenantId(tenantSelectionScopeId, store.id);
        dispatch(updateAdminData({ tenantId: store.id }));
        navigate(`${pathname}?tenantId=${store.id}`, { replace: true, state: { tenantId: store.id } });
        return;
      }

      setSelectedStore(null);
      writeSelectedTenantId(tenantSelectionScopeId, null);
      dispatch(setAdminData(null));
      navigate(pathname, { replace: true, state: { tenantId: null } });
    },
    [dispatch, location?.pathname, navigate, tenantSelectionScopeId]
  );

  useEffect(() => {
    if (!isAdmin || allStores.length === 0 || !tenantSelectionScopeId) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const urlTenantId = params.get('tenantId');
    const cachedTenantId = readSelectedTenantId(tenantSelectionScopeId);
    const resolvedTenantId = urlTenantId || cachedTenantId;

    if (!resolvedTenantId) {
      if (selectedStore !== null) {
        setSelectedStore(null);
      }
      if (adminData?.tenantId) {
        dispatch(setAdminData(null));
      }
      return;
    }

    const tenant = allStores.find((store) => store.id === resolvedTenantId);
    if (!tenant) {
      writeSelectedTenantId(tenantSelectionScopeId, null);
      return;
    }

    if (selectedStore?.id !== tenant.id) {
      setSelectedStore(tenant);
    }

    if (adminData?.tenantId !== tenant.id) {
      dispatch(updateAdminData({ tenantId: tenant.id }));
    }

    if (!urlTenantId) {
      writeSelectedTenantId(tenantSelectionScopeId, tenant.id);
      navigate(`${location.pathname}?tenantId=${tenant.id}`, { replace: true });
      return;
    }

    if (urlTenantId !== cachedTenantId) {
      writeSelectedTenantId(tenantSelectionScopeId, urlTenantId);
    }
  }, [
    adminData?.tenantId,
    allStores,
    dispatch,
    isAdmin,
    location.pathname,
    location.search,
    navigate,
    selectedStore,
    tenantSelectionScopeId,
  ]);

  const searchFilterComponent = useMemo(() => {
    return isAdmin ? (
      <div
        style={{
          backgroundColor: "#f2f4f7",
          transition: "all 0.2s ease-out",
          position: "sticky",
          marginLeft: isCollapsed ? "55px" : "250px",
          top: "60px",
          right: "0",
          zIndex: 1000,
          padding: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Select
            value={{ value: selectedStore, label: selectedStore?.tenantName || selectedStore?.name }}
            options={allStores.map((store) => ({ value: store, label: store?.tenantName || store?.name }))}
            onChange={(opt) => {
              onSelectTenant(opt?.value);
            }}
            isClearable={!!selectedStore}
            menuPlacement="bottom"
            maxMenuHeight={200}
            formatOptionLabel={({ label }) => (
              <div style={{ display: "flex" }}>
                <div>{t(label)}</div>
              </div>
            )}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                maxWidth: "300px",
                minWidth: "300px",
                flexShrink: "1",
              }),
            }}
          />
          {(selectedStore !== undefined) ?
            <Button type="button" onClick={() => selectedStore && onSelectTenant()} style={{ margin: 0 }} size='sm'>{t('admin.go_back')}</Button> : <></>}
          <Typography className="">{t('admin.selected_tenant')}: {selectedStore?.tenantName || selectedStore?.name || t('admin.all_tenants')}</Typography>
        </div>
      </div>
    ) : (
      <></>
    );
  }, [isAdmin, isCollapsed, onSelectTenant, selectedStore, allStores, t]);

  return { searchFilterComponent };
};

const useCityMailerLiteConnectedUsers = (cityId, lastSyncedTimestamp) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [cityMailerLiteConnectedUsers, setCityMailerLiteConnectedUsers] = useState([]);
  const [lastSyncedDate, setLastSyncedDate] = useState();

  const getCityMailerLiteConnectedUsers = useCallback(async () => {
    if (!cityId) return;

    try {
      setIsLoading(true);

      const res = await fetchCityMailerLiteConnectedUsers(cityId);

      if (res) {
        setCityMailerLiteConnectedUsers(res);
      }
      setIsLoading(false);
    } catch (error) {
      console.log("error", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    getCityMailerLiteConnectedUsers();
  }, [getCityMailerLiteConnectedUsers]);


  useEffect(() => {
    if (lastSyncedTimestamp) {
      setLastSyncedDate(new Date(lastSyncedTimestamp));
    }
  }, [lastSyncedTimestamp]);

  const syncCityMailerLiteConnectedUsers = useCallback(async () => {
    if (!cityId) return;

    try {
      setIsLoading(true);

      const res = await syncMailerLiteSubscriptions(cityId);
      console.log('res', res)
      setLastSyncedDate(new Date(res));
      dispatch(setDetails({ lastSyncMailerLiteSubscriptionsTimestamp: res }));

      await getCityMailerLiteConnectedUsers();
      setIsLoading(false);
    } catch (error) {
      console.log("error", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [cityId, dispatch, getCityMailerLiteConnectedUsers]);

  return { cityMailerLiteConnectedUsers, isLoading, syncCityMailerLiteConnectedUsers, lastSyncedDate };
};

const useFetchDashboardOrganisations = ({ ownerType, ownerId, challenge, startDate, endDate, filterBy, branchId, region, geographicalAgrerationLevel, transportModes = [], skipOrganisationsData = false, serverSide = false, pageSize = 10 }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [organisations, setOrganisations] = useState();
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSort, setCurrentSort] = useState({ column: 'ghg', direction: 'desc' });

  const normalizeDashboardDate = useCallback((value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value?.toDate === "function") {
      return value.toDate();
    }

    if (typeof value === "string") {
      const parsed = new Date(value.includes("T") ? value : `${value}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value === "number") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  }, []);

  const getOrganisations = useCallback(async () => {
    if (!ownerId) return;
    if (skipOrganisationsData) return;
    if ((filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.range.logType && !endDate) || (filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.challenges.logType && !challenge)) {
      return;
    }
    setIsLoading(true);

    try {
      const startDateUpdated = filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.challenges.logType ? dateUtils.getFormattedStringWeekDayDate(challenge.startAt) : dateUtils.getFormattedStringWeekDayDate(dateUtils.getNearestStartDateByFilter(filterBy.logType, startDate));
      const endDateUpdated = filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.challenges.logType ? dateUtils.getFormattedStringWeekDayDate(challenge.endAt) : getEndDateForDashboard({ startDate: startDateUpdated, endDate, period: filterBy.logType });

      // Map frontend column IDs to backend field names
      const sortByMapping = {
        'ghg': 'totalGreenhouseGazes',
        'trips': 'sustainableSessionCount',
        'users': 'usersCount'
      };

      const res = await fetchDashboardOrganisations({
        ownerType,
        ownerId,
        challengeId: challenge?.id || '',
        startDate: startDateUpdated,
        endDate: endDateUpdated,
        branchId,
        ...(serverSide && {
          page: currentPage + 1,
          limit: pageSize,
          sortBy: sortByMapping[currentSort.column] || currentSort.column,
          sortOrder: currentSort.direction.toUpperCase()
        })
      });

      if (res) {
        if (serverSide && res && res !== undefined) {
          // Server-side pagination response: { data: [], total: number }
          setOrganisations(res);
          setTotalRecords(res[0].total_users);
        } else {
          // Legacy response: array of organizations
          setOrganisations(Array.isArray(res) ? res : []);
          setTotalRecords(Array.isArray(res) ? res.length : 0);
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.error("error", error);
      setIsLoading(false);
    }
  }, [ownerId, filterBy.logType, endDate, startDate, ownerType, challenge, branchId, skipOrganisationsData, serverSide, currentPage, pageSize, currentSort.column, currentSort.direction]);

  useEffect(() => {
    getOrganisations();
  }, [getOrganisations]);

  const getOrganisationsForExcel = useCallback(async () => {
    if (!ownerId) return;
    if ((filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.range.logType && !endDate) || (filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.challenges.logType && !challenge)) {
      return;
    }
    setIsLoading(true);

    try {
      const normalizedStartDate = filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.challenges.logType
        ? normalizeDashboardDate(challenge.startAt)
        : normalizeDashboardDate(dateUtils.getNearestStartDateByFilter(filterBy.logType, startDate));
      const normalizedEndDate = filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.challenges.logType
        ? normalizeDashboardDate(challenge.endAt)
        : normalizeDashboardDate(endDate);

      const startDateUpdated = normalizedStartDate ? dateUtils.getFormattedStringWeekDayDate(normalizedStartDate) : undefined;
      const endDateUpdated = filterBy.logType === DASHBOARD_AVAILABLE_FILTER_TYPES.challenges.logType
        ? (normalizedEndDate ? dateUtils.getFormattedStringWeekDayDate(normalizedEndDate) : undefined)
        : getEndDateForDashboard({ startDate: normalizedStartDate, endDate: normalizedEndDate, period: filterBy.logType });

      const res = await fetchCityDashboardOrganisationsForExport({
        ownerType,
        ownerId,
        challengeId: challenge?.id || '',
        startDate: startDateUpdated,
        endDate: endDateUpdated,
        branchId,
        region,
        geographicalAgrerationLevel,
        transportModes,
      });

      setIsLoading(false);
      return res;
    } catch (error) {
      console.error("error", error);
      setIsLoading(false);
      return [];
    }
  }, [ownerId, filterBy.logType, endDate, challenge, startDate, ownerType, branchId, region, geographicalAgrerationLevel, transportModes, normalizeDashboardDate]);

  // Page change handler for server-side pagination
  const handlePageChange = useCallback((newPageIndex) => {
    setCurrentPage(newPageIndex);
  }, []);

  // Sort change handler for server-side sorting
  const handleSortChange = useCallback((sortBy) => {
    const columnId = sortBy[0]?.id;

    // Only allow sorting on specific columns: ghg, trips, users
    const allowedSortColumns = ['ghg', 'trips', 'users'];
    if (!allowedSortColumns.includes(columnId)) {
      return; // Do nothing if column is not in allowed list
    }

    setCurrentSort(prevSort => {
      // If clicking the same column, toggle direction
      if (prevSort.column === columnId) {
        const newDirection = prevSort.direction === 'asc' ? 'desc' : 'asc';
        return { column: columnId, direction: newDirection };
      } else {
        // If clicking a different column, start with asc
        return { column: columnId || '', direction: 'asc' };
      }
    });
    // Reset to first page when sorting changes
    setCurrentPage(0);
  }, []);

  return {
    organisations,
    isLoading,
    setOrganisations,
    setIsLoading,
    getOrganisationsForExcel,
    fetchOrganisations: getOrganisations,
    // Server-side pagination props
    totalRecords,
    currentPage,
    handlePageChange,
    // Server-side sorting props
    currentSort,
    handleSortChange,
    serverSide
  };
};


const cityHooks = {
  useUpdateCityLimitSettings,
  useUpdateCitizenLimitSettings,
  useFetchCityLimitSettings,
  useFetchCitizenLimitSettings,
  useDeleteUser,
  useDeaffiliateUser,
  useFetchTenantStores,
  useAdminDataForTenant,
  useCityMailerLiteSettings,
  useCityMailerLiteConnectedUsers,
  useFetchDashboardOrganisations,
};

export default cityHooks;
